import { Injectable, NgZone } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import {
    debounceTime,
    filter,
    map,
    mergeMap,
    switchMap,
    tap,
    withLatestFrom,
} from 'rxjs/operators';

import { AuthService } from '../auth/auth.service';
import { from, Observable } from 'rxjs';
import { PostProcessSSOLoginAction } from './go.actions';
import { SessionService } from '../session/session.service';
import { UserDataService } from '@core/data/api/user/user-data.service';
import { IAppState } from '../app/app-state';
import { getPermissions } from '../session/session-selectors';
import { PERMISSION } from '@core/auth/permission';
import { getRouterState } from '../router/router-selectors';
import { RouterGoAction } from '../router/router-actions';
import { FetchUserAction, SetUserPermissionsAction } from '../session/session-actions';
import { getUserAuthentication } from '../auth/auth-selectors';
import { ProcessPostLoginAction } from '../app/app-actions';
import { LocalStorageService } from '@core/local-storage/local-storage.service';
import { STORE_COMPANY_PREFS, STORE_USER_PREFS } from '@core/local-storage/local-storage';
import { $log } from '@core/utils/logger';
import { GO_POST_PROCESS_LOGIN, GO_INIT, ROUTER_NAVIGATION } from '../action-types';
import { getUser } from '../session/session-selectors';
import { PAYROLL, GO_SSO } from '@core/router/app-states';
import { ConnectionPermission } from '@core/models/connection/connection-permission';
import { LogoutAction } from '../auth/login/login-actions';

@Injectable()
export class GoEffects {
    constructor(
        private actions$: Actions,
        private authService: AuthService,
        private localStorageService: LocalStorageService,
        private sessionService: SessionService,
        private ngZone: NgZone,
        private userDataService: UserDataService,
        private store$: Store<IAppState>
    ) {}

    postProcessLogin$ = createEffect(() => 
        this.actions$.pipe(
            ofType<PostProcessSSOLoginAction>(GO_POST_PROCESS_LOGIN),
            withLatestFrom(
                this.store$.select(getPermissions),
                this.authService.isAuthorised(PERMISSION.MANAGE_SCHEDULE),
                this.authService.isAuthorised(PERMISSION.MANAGE_TIMECARD),
                this.authService.isAuthorised(PERMISSION.MANAGE_ATTENDANCE_SETTINGS)
            ),
            mergeMap(
                ([
                    action,
                    permissions,
                    canManageSchedule,
                    canManageTimecards,
                    canManageAttendanceSettings,
                ]) => {
                    const redirectUri: string = action.payload.redirectUri;

                    $log.debug('SSO permissions changed..', permissions);
                    let actions: Action[] = [];

                    if (
                        (!redirectUri || redirectUri === '#payroll') &&
                        canManageSchedule
                    ) {
                        actions.push(
                            new RouterGoAction({
                                name: PAYROLL,
                            })
                        );
                    } else {
                        // Default to payroll
                        actions.push(
                            new RouterGoAction({
                                name: PAYROLL,
                            })
                        );
                    }

                    return from(actions);
                }
            )
        )
    );

    goSSO$ = createEffect(() => 
        this.actions$.pipe(
            ofType(ROUTER_NAVIGATION, GO_INIT),
            map((action: any) => action.payload),
            withLatestFrom(
                this.store$.select(getRouterState),
                this.store$.select(getUserAuthentication)
            ),
            filter(
                ([routerPayload, routerState, auth]) => routerState.name === GO_SSO
            ),
            tap(([routerPayload, routerState, auth]) => {

                // Verify if there's a logged-in user and clean up
                if (auth?.access_token) {
                    this.localStorageService.setItem(STORE_USER_PREFS, {});

                    this.localStorageService.setItem(STORE_COMPANY_PREFS, {});
                }
            }),
            debounceTime(200),
            switchMap(([routerPayload, routerState, auth]) => {
                const searchParams: any = routerState.params;
                const ssoMode: string = searchParams.mode;
                const ssoProvider: string = searchParams.service_provider;
                const ssoId: string = searchParams.sso_id;

                return (this.authService.loginViaSSO(ssoId, ssoProvider)
                    .toObservable() as Observable<any>)
                    .pipe(
                        withLatestFrom(this.store$.select(getUser)),
                        switchMap(([loginCompanyResponse, user]: [any, any]) => {

                            const userId =
                                user?.id || loginCompanyResponse.payload?.user_id;

                            return this.userDataService
                                .fetchUserPermissions(userId)
                                .pipe(
                                    switchMap((res: any) => {
                                        return [
                                            new SetUserPermissionsAction(
                                                (
                                                    res.permissions || []
                                                ).map((permission) =>
                                                    Object.assign(
                                                        new ConnectionPermission(),
                                                        permission
                                                    )
                                                )
                                            ),
                                            new PostProcessSSOLoginAction({
                                                userId,
                                                redirectUri:
                                                    loginCompanyResponse.payload
                                                        ?.redirect_uri,
                                            })
                                        ];
                                    })
                                );
                        })
                    );
                        
            })
        )
    
    );
};

